import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Assessment as AssessmentIcon,
  GetApp as DownloadIcon,
  DateRange as DateRangeIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  ExpandMore as ExpandMoreIcon,
  Analytics as AnalyticsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { ClassService } from '../../services/classService';
import { 
  ReportService, 
  ReportFilters, 
  AttendanceData, 
  StudentAttendanceReport, 
  ClassReport, 
  ReportSummary 
} from '../../services/reportService';
import { ExportService } from '../../services/exportService';
import { Class } from '../../types';

export function Reports() {
  const { user } = useAuth();
  const [reportType, setReportType] = useState('');
  const [dateRange, setDateRange] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [classes, setClasses] = useState<Class[]>([]);
  const [quickStats, setQuickStats] = useState({
    todayAttendance: 0,
    weeklyAttendance: 0,
    monthlyAttendance: 0,
    totalReports: 0
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });

  useEffect(() => {
    loadClasses();
    loadQuickStats();
  }, []);

  const loadClasses = async () => {
    try {
      const result = await ClassService.getClasses(100);
      setClasses(result.classes);
    } catch (error) {
      console.error('Failed to load classes:', error);
    }
  };

  const loadQuickStats = async () => {
    try {
      const stats = await ReportService.getQuickStats();
      setQuickStats(stats);
    } catch (error) {
      console.error('Failed to load quick stats:', error);
    }
  };

  const reportTypes = [
    { value: 'attendance', label: 'Attendance Report', icon: <AssessmentIcon /> },
    { value: 'student', label: 'Student Report', icon: <PersonIcon /> },
    { value: 'class', label: 'Class Report', icon: <SchoolIcon /> },
    { value: 'summary', label: 'Summary Report', icon: <TrendingUpIcon /> },
  ];

  const dateRanges = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'semester', label: 'This Semester' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const exportFormats = [
    { value: 'pdf', label: 'PDF', icon: <PdfIcon />, color: 'error' },
    { value: 'excel', label: 'Excel', icon: <ExcelIcon />, color: 'success' },
  ];

  const handleGenerateReport = async () => {
    if (!reportType || !dateRange) {
      showSnackbar('Please select report type and date range', 'error');
      return;
    }
    setLoading(true);
    try {
      console.log('Generating report with filters:', {
        reportType,
        dateRange,
        selectedClass,
        startDate,
        endDate
      });
      const filters: ReportFilters = {
        reportType: reportType as any,
        dateRange: dateRange as any,
        startDate: startDate ? new Date(startDate) : undefined,
        endDate: endDate ? new Date(endDate) : undefined,
        classId: selectedClass || undefined,
      };
      const data = await ReportService.generateReport(filters);
      console.log('Report data generated:', data);
      
      if (Array.isArray(data) && data.length === 0) {
        showSnackbar('No data found for the selected criteria. Try a different date range.', 'warning');
      } else if (data && (Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0)) {
        setReportData(data);
        showSnackbar('Report generated successfully', 'success');
      } else {
        showSnackbar('No data found for the selected criteria', 'warning');
      }
    } catch (error: any) {
      console.error('Error generating report:', error);
      showSnackbar(error.message || 'Failed to generate report', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickReport = async (type: string) => {
    setReportType(type);
    setDateRange('week');
    setSelectedClass('');
    
    const filters: ReportFilters = {
      reportType: type as any,
      dateRange: 'week',
    };

    setLoading(true);
    try {
      const data = await ReportService.generateReport(filters);
      setReportData(data);
      showSnackbar(`${type.charAt(0).toUpperCase() + type.slice(1)} report generated`, 'success');
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to generate report', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleExport = async (format: 'excel' | 'pdf') => {
    if (!reportData || !reportType) {
      showSnackbar('No report data to export. Please generate a report first.', 'error');
      return;
    }
    try {
      ExportService.exportReport(reportType, format, reportData);
      showSnackbar(`Report exported successfully as ${format.toUpperCase()}`, 'success');
    } catch (error: any) {
      console.error('Export error:', error);
      showSnackbar(error.message || `Failed to export report as ${format.toUpperCase()}`, 'error');
    }
  };

  const getAttendanceColor = (rate: number) => {
    if (rate >= 90) return 'success';
    if (rate >= 75) return 'warning';
    return 'error';
  };

  const renderAttendanceReport = (data: AttendanceData[]) => (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentIcon color="primary" />
        Attendance Records ({data.length})
      </Typography>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Student Name</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.slice(0, 100).map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record.studentName}</TableCell>
                <TableCell>{record.className}</TableCell>
                <TableCell>{record.date.toLocaleDateString()}</TableCell>
                <TableCell>
                  <Chip 
                    label={record.status}
                    color={record.status === 'Present' ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {data.length > 100 && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
          Showing first 100 records of {data.length} total records
        </Typography>
      )}
    </Paper>
  );

  const renderStudentReport = (data: StudentAttendanceReport[]) => (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon color="primary" />
        Student Attendance Report ({data.length} students)
      </Typography>
      
      {data.map((student) => (
        <Accordion key={student.studentId} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Typography variant="subtitle1" fontWeight="medium">
                {student.studentName}
              </Typography>
              <Chip label={student.rollNumber} size="small" variant="outlined" />
              <Box sx={{ flexGrow: 1 }} />
              <Chip 
                label={`${student.attendanceStats.attendanceRate}%`}
                color={getAttendanceColor(student.attendanceStats.attendanceRate)}
                size="small"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Attendance Statistics</Typography>
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Present Sessions</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {student.attendanceStats.presentSessions}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Total Sessions</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {student.attendanceStats.totalSessions}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={student.attendanceStats.attendanceRate} 
                    color={getAttendanceColor(student.attendanceStats.attendanceRate)}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Recent Attendance</Typography>
                <List dense>
                  {student.recentAttendance.slice(-5).map((record, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemText 
                        primary={record.date.toLocaleDateString()}
                        secondary={record.className}
                      />
                      <Chip 
                        label={record.status}
                        color={record.status === 'Present' ? 'success' : 'error'}
                        size="small"
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );

  const renderClassReport = (data: ClassReport[]) => (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SchoolIcon color="primary" />
        Class Attendance Report ({data.length} classes)
      </Typography>
      
      <Grid container spacing={3}>
        {data.map((classReport) => (
          <Grid item xs={12} md={6} key={classReport.classId}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {classReport.className}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Total Students: {classReport.totalStudents}
                  </Typography>
                  <Chip 
                    label={`${classReport.attendanceStats.attendanceRate}%`}
                    color={getAttendanceColor(classReport.attendanceStats.attendanceRate)}
                    size="small"
                  />
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={classReport.attendanceStats.attendanceRate} 
                  color={getAttendanceColor(classReport.attendanceStats.attendanceRate)}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', typography: 'body2' }}>
                  <span>Present: {classReport.attendanceStats.presentSessions}</span>
                  <span>Total: {classReport.attendanceStats.totalSessions}</span>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  const renderSummaryReport = (data: ReportSummary) => (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIcon color="primary" />
        Summary Report
      </Typography>
      
      {/* Overall Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{data.totalClasses}</Typography>
              <Typography variant="body2" color="textSecondary">Total Classes</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{data.totalStudents}</Typography>
              <Typography variant="body2" color="textSecondary">Total Students</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color={getAttendanceColor(data.overallAttendanceRate)}>
                {data.overallAttendanceRate}%
              </Typography>
              <Typography variant="body2" color="textSecondary">Overall Attendance</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{data.lowAttendanceStudents.length}</Typography>
              <Typography variant="body2" color="textSecondary">Low Attendance</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Performing Classes */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" />
                Top Performing Classes
              </Typography>
              <List>
                {data.topPerformingClasses.map((classItem, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={classItem.className}
                      secondary={`Rank #${index + 1}`}
                    />
                    <Chip 
                      label={`${classItem.attendanceRate}%`}
                      color="success"
                      size="small"
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" />
                Students Needing Attention
              </Typography>
              <List>
                {data.lowAttendanceStudents.slice(0, 5).map((student, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={student.studentName}
                      secondary={student.className}
                    />
                    <Chip 
                      label={`${student.attendanceRate}%`}
                      color="error"
                      size="small"
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderReportData = () => {
    if (!reportData) return null;

    switch (reportType) {
      case 'attendance':
        return renderAttendanceReport(reportData);
      case 'student':
        return renderStudentReport(reportData);
      case 'class':
        return renderClassReport(reportData);
      case 'summary':
        return renderSummaryReport(reportData);
      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Reports & Analytics
      </Typography>

      {user?.role !== 'admin' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Faculty members can view reports for their assigned classes only.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Report Generation */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="primary" />
              Generate Report
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Report Type</InputLabel>
                  <Select
                    value={reportType}
                    label="Report Type"
                    onChange={(e) => setReportType(e.target.value)}
                  >
                    {reportTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type.icon}
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRange}
                    label="Date Range"
                    onChange={(e) => setDateRange(e.target.value)}
                  >
                    {dateRanges.map((range) => (
                      <MenuItem key={range.value} value={range.value}>
                        {range.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {user?.role === 'admin' && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Class (Optional)</InputLabel>
                    <Select
                      value={selectedClass}
                      label="Class (Optional)"
                      onChange={(e) => setSelectedClass(e.target.value)}
                    >
                      <MenuItem value="">All Classes</MenuItem>
                      {classes.map((classItem) => (
                        <MenuItem key={classItem.id} value={classItem.id}>
                          {classItem.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {dateRange === 'custom' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <AnalyticsIcon />}
                  onClick={handleGenerateReport}
                  disabled={!reportType || !dateRange || loading}
                  sx={{ mr: 2 }}
                >
                  {loading ? 'Generating...' : 'Generate Report'}
                </Button>
                
                {reportData && (
                  <Box sx={{ display: 'inline-flex', gap: 1, ml: 2 }}>
                    {exportFormats.map((format) => (
                      <Button
                        key={format.value}
                        variant="outlined"
                        startIcon={format.icon}
                        color={format.color as any}
                        size="small"
                        onClick={() => handleExport(format.value as 'excel' | 'pdf')}
                      >
                        {format.label}
                      </Button>
                    ))}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Quick Reports & Stats */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Quick Reports
            </Typography>
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleQuickReport('summary')}>
                  <ListItemIcon>
                    <TrendingUpIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Weekly Summary"
                    secondary="This week's overview"
                  />
                </ListItemButton>
              </ListItem>
              <Divider />
              
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleQuickReport('class')}>
                  <ListItemIcon>
                    <SchoolIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Class Reports"
                    secondary="All classes performance"
                  />
                </ListItemButton>
              </ListItem>
              <Divider />
              
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleQuickReport('student')}>
                  <ListItemIcon>
                    <PersonIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Student Analysis"
                    secondary="Individual performance"
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Paper>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="primary" />
                Quick Stats
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Today's Attendance</Typography>
                  <Chip 
                    label={`${quickStats.todayAttendance}%`} 
                    size="small" 
                    color={getAttendanceColor(quickStats.todayAttendance)}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Weekly Average</Typography>
                  <Chip 
                    label={`${quickStats.weeklyAttendance}%`} 
                    size="small" 
                    color={getAttendanceColor(quickStats.weeklyAttendance)}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Monthly Average</Typography>
                  <Chip 
                    label={`${quickStats.monthlyAttendance}%`} 
                    size="small" 
                    color={getAttendanceColor(quickStats.monthlyAttendance)}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Report Display */}
        {reportData && (
          <Grid item xs={12}>
            {renderReportData()}
          </Grid>
        )}
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
} 